import type { RevitFamily } from '../../../shared/api/revit/types';
import { familyMatchesAnyObjectType } from '../../../shared/lib/hooks/parameterAddition/familyParameterAvailability';
import {
  areProjectParametersAvailableForBindingAndCategories,
  doesProjectParameterDescriptorCoverBindingAndCategories,
  findAuthoritativeProjectParameterBindingKind,
  type AvailableProjectParameterDescriptor,
} from '../../../shared/lib/revit/projectParameterMutationPlan';
import {
  areDataTypesCompatible,
  familyHasCompatibleParameter,
  getFamilyParameterDescriptorInfo,
  normalizeFamilyCategoryKey,
} from '../../../shared/lib/revit/familyParameterTargetSelection';
import {
  getEquivalentRevitParameterNames,
  normalizeRevitParameterName,
  sanitizeRevitParameterNames,
} from '../../../shared/lib/revitParameterNames';
import type { MappingProjectReadinessDiff } from './mappingProjectReadiness';
import type {
  MappingParameterScopeRequirement,
  MappingParameterRequirementTarget,
} from './selectedMappingParameterScopeRequirements';
import type { MappingProjectParameterReference } from './readiness/mappedParameterReferences';

export type MappingParameterAssessmentStatus =
  | 'satisfied'
  | 'missing'
  | 'conflict'
  | 'not-applicable';

export interface MappingParameterScopeSatisfaction {
  requirementKey: string;
  satisfied: boolean;
}

export interface MappingParameterAssessment extends MappingParameterScopeSatisfaction {
  status: MappingParameterAssessmentStatus;
  targetKind: MappingParameterRequirementTarget['kind'];
  matchingEntityCount: number;
  conflicts: Array<{
    entityId: string;
    entityName: string;
    actualBindingKind: 'type' | 'instance' | null;
    reason: 'binding' | 'dataType';
  }>;
}

function normalizeName(value: string): string {
  return value.trim().toLowerCase();
}

const NATIVE_INSULATION_PARAMETER_NAMES = new Set([
  'insulation thickness',
  'insulation type',
]);

const NATIVE_PROJECT_INFORMATION_PARAMETER_NAMES: ReadonlySet<string> = new Set([
  'client name',
  'project address',
  'project name',
]);

function requirementParameterIdentity(value: string): string {
  return normalizeName(normalizeRevitParameterName(value));
}

function equivalentNameLookup(parameterName: string): Set<string> {
  return new Set(getEquivalentRevitParameterNames(parameterName).map(normalizeName));
}

function expectedDataType(requirement: MappingParameterScopeRequirement): string | undefined {
  const names = equivalentNameLookup(requirement.parameterName);
  return requirement.parameterTypeHints.find((hint) =>
    names.has(normalizeName(hint.name))
  )?.dataType;
}

function descriptorMatchesDataType(
  requirement: MappingParameterScopeRequirement,
  descriptor: AvailableProjectParameterDescriptor
): boolean {
  const expected = expectedDataType(requirement)?.trim().toUpperCase();
  return !expected || descriptor.dataType?.trim().toUpperCase() === expected;
}

function isAuthoritativeProjectDescriptor(
  descriptor: AvailableProjectParameterDescriptor
): boolean {
  return descriptor.isProjectBinding === true ||
    descriptor.group?.trim().toLowerCase() === 'project information';
}

function isNativeProjectInformationDescriptor(
  descriptor: AvailableProjectParameterDescriptor
): boolean {
  return descriptor.parameterType === 'Instance' &&
    descriptor.group?.trim().toLowerCase() === 'project information' &&
    NATIVE_PROJECT_INFORMATION_PARAMETER_NAMES.has(normalizeName(descriptor.name));
}

function isNativeProjectInformationRequirement(
  requirement: MappingParameterScopeRequirement,
  descriptor: AvailableProjectParameterDescriptor
): boolean {
  return requirement.area === 'project' &&
    requirement.repairKind === 'processProject' &&
    requirement.target.kind === 'projectCategories' &&
    requirement.target.categories.length > 0 &&
    requirement.target.categories.every(
      (category) => normalizeName(category) === 'project information'
    ) &&
    isNativeProjectInformationDescriptor(descriptor);
}

function descriptorCoversRequirementTarget(
  requirement: MappingParameterScopeRequirement,
  descriptor: AvailableProjectParameterDescriptor
): boolean {
  const actualBindingKind = descriptor.parameterType === 'Instance' ? 'instance' : 'type';
  return doesProjectParameterDescriptorCoverBindingAndCategories(
    descriptor,
    actualBindingKind,
    requirement.target.kind === 'projectCategories' ? requirement.target.categories : []
  );
}

export function assessProjectParameterRequirements(
  requirements: MappingParameterScopeRequirement[],
  availableParameters: AvailableProjectParameterDescriptor[],
  sampledParameterNames: string[] = []
): MappingParameterAssessment[] {
  const hasAuthoritativeMetadata = availableParameters.some(
    (parameter) =>
      typeof parameter.isProjectBinding === 'boolean' || Array.isArray(parameter.categories)
  );
  const sampledLookup = new Set(sampledParameterNames.map(normalizeName));

  return requirements
    .filter((requirement) => requirement.target.kind === 'projectCategories')
    .map((requirement) => {
      const names = equivalentNameLookup(requirement.parameterName);
      // Repair adopts the existing authoritative binding for processProject
      // parameters and never rebinds (adoptRequestBindingKinds); the assessment
      // must accept the same kind, or the conflict becomes terminal.
      const effectiveBindingKind =
        requirement.repairKind === 'processProject'
          ? findAuthoritativeProjectParameterBindingKind(
              requirement.parameterName,
              availableParameters,
              requirement.parameterTypeHints
            ) ?? requirement.bindingKind
          : requirement.bindingKind;
      const matchingDescriptors = availableParameters.filter((descriptor) =>
        names.has(normalizeName(descriptor.name))
      );
      const authoritative = matchingDescriptors.filter(isAuthoritativeProjectDescriptor);
      const matchingTargetDescriptors = authoritative.filter((descriptor) =>
        descriptorCoversRequirementTarget(requirement, descriptor)
      );
      const exactSatisfied = areProjectParametersAvailableForBindingAndCategories(
        [requirement.parameterName],
        effectiveBindingKind,
        requirement.target.kind === 'projectCategories'
          ? requirement.target.categories
          : [],
        availableParameters,
        requirement.parameterTypeHints
      );
      const sampled = Array.from(names).some((name) => sampledLookup.has(name));
      const conflicts: MappingParameterAssessment['conflicts'] = [];
      matchingTargetDescriptors.forEach((descriptor) => {
        const actualBindingKind = descriptor.parameterType === 'Instance' ? 'instance' : 'type';
        if (isNativeProjectInformationRequirement(requirement, descriptor)) {
          return;
        }
        if (actualBindingKind !== effectiveBindingKind) {
          conflicts.push({
            entityId: descriptor.name,
            entityName: descriptor.name,
            actualBindingKind,
            reason: 'binding' as const,
          });
          return;
        }
        if (!descriptorMatchesDataType(requirement, descriptor)) {
          conflicts.push({
            entityId: descriptor.name,
            entityName: descriptor.name,
            actualBindingKind,
            reason: 'dataType' as const,
          });
        }
      });
      const legacySatisfied = matchingDescriptors.length > 0 || sampled;
      const sampledNativeProperty = sampled && NATIVE_INSULATION_PARAMETER_NAMES.has(
        normalizeName(requirement.parameterName)
      );
      const nativeProjectInformationProperty = matchingTargetDescriptors.some(
        (descriptor) => isNativeProjectInformationRequirement(requirement, descriptor)
      );
      const satisfied = hasAuthoritativeMetadata
        ? exactSatisfied || sampledNativeProperty || nativeProjectInformationProperty
        : legacySatisfied;
      return {
        requirementKey: requirement.key,
        satisfied,
        status: satisfied ? 'satisfied' : conflicts.length > 0 ? 'conflict' : 'missing',
        targetKind: 'projectCategories',
        matchingEntityCount: matchingTargetDescriptors.length,
        conflicts,
      };
    });
}

function familyMatchesTarget(
  family: RevitFamily,
  target: Exclude<MappingParameterRequirementTarget, { kind: 'projectCategories' }>
): boolean {
  if (!family?.id || family.isSystemFamily || family.isEditable === false) {
    return false;
  }

  if (target.kind === 'editableFamilyCategories') {
    const categories = new Set(target.categories.map(normalizeFamilyCategoryKey));
    return categories.has(normalizeFamilyCategoryKey(family.category));
  }

  return familyMatchesAnyObjectType(
    family,
    target.objectTypes,
    target.discriminatorParameterName,
    target.discriminatorBindingKind
  );
}

export function collectFamilyRequirementSnapshotParameterNames(
  requirements: MappingParameterScopeRequirement[]
): string[] {
  return sanitizeRevitParameterNames(
    requirements.flatMap((requirement) => requirement.target.kind === 'familyObjectTypes'
      ? [requirement.parameterName, requirement.target.discriminatorParameterName]
      : requirement.target.kind === 'editableFamilyCategories'
        ? [requirement.parameterName]
        : [])
  );
}

export function resolveFamilyRequirementTargetIds(
  families: RevitFamily[],
  requirements: MappingParameterScopeRequirement[]
): string[] {
  const familyRequirements = requirements.filter(
    (requirement) => requirement.target.kind !== 'projectCategories'
  );
  return Array.from(new Set(families
    .filter((family) => familyRequirements.some((requirement) =>
      requirement.target.kind !== 'projectCategories' &&
      familyMatchesTarget(family, requirement.target)
    ))
    .map((family) => family.id)
    .filter(Boolean)));
}

export function assessFamilyParameterRequirements(
  requirements: MappingParameterScopeRequirement[],
  families: RevitFamily[]
): MappingParameterAssessment[] {
  return requirements
    .filter((requirement) => requirement.target.kind !== 'projectCategories')
    .map((requirement) => {
      const target = requirement.target;
      if (target.kind === 'projectCategories') {
        throw new Error('Project requirements cannot be evaluated as family requirements.');
      }
      const matchingFamilies = families.filter((family) => familyMatchesTarget(family, target));
      if (matchingFamilies.length === 0) {
        const satisfied = target.emptyMatchPolicy === 'non-applicable';
        return {
          requirementKey: requirement.key,
          satisfied,
          status: satisfied ? 'not-applicable' : 'missing',
          targetKind: target.kind,
          matchingEntityCount: 0,
          conflicts: [],
        };
      }

      const expected = expectedDataType(requirement);
      const conflicts: MappingParameterAssessment['conflicts'] = [];
      matchingFamilies.forEach((family) => {
        const descriptor = getFamilyParameterDescriptorInfo(family, requirement.parameterName);
        if (!descriptor) {
          return;
        }
        if (descriptor.bindingKind !== requirement.bindingKind) {
          conflicts.push({
            entityId: family.id,
            entityName: family.name,
            actualBindingKind: descriptor.bindingKind,
            reason: 'binding' as const,
          });
          return;
        }
        if (expected && !areDataTypesCompatible(descriptor.dataType, expected)) {
          conflicts.push({
            entityId: family.id,
            entityName: family.name,
            actualBindingKind: descriptor.bindingKind,
            reason: 'dataType' as const,
          });
        }
      });
      const satisfied = matchingFamilies.every((family) => familyHasCompatibleParameter(
        family,
        requirement.parameterName,
        requirement.bindingKind,
        expected
      ));
      return {
        requirementKey: requirement.key,
        satisfied,
        status: satisfied ? 'satisfied' : conflicts.length > 0 ? 'conflict' : 'missing',
        targetKind: target.kind,
        matchingEntityCount: matchingFamilies.length,
        conflicts,
      };
    });
}

function describeRequirementTarget(requirement: MappingParameterScopeRequirement): string {
  if (requirement.target.kind === 'projectCategories') {
    return `project categories ${requirement.target.categories.join(', ')}`;
  }
  if (requirement.target.kind === 'editableFamilyCategories') {
    return `editable ${requirement.target.categories.join(', ')} families`;
  }
  return `${requirement.target.objectTypes.join(', ')} families classified by ${requirement.target.discriminatorParameterName}`;
}

function describeConflictResolution(
  conflictedRequirements: ReadonlySet<MappingParameterScopeRequirement>
): string {
  let hasFamilyConflict = false;
  let hasProjectConflict = false;
  conflictedRequirements.forEach((requirement) => {
    if (requirement.target.kind === 'projectCategories') {
      hasProjectConflict = true;
    } else {
      hasFamilyConflict = true;
    }
  });
  if (hasFamilyConflict && hasProjectConflict) {
    return ' Resolve the conflicting bindings in Revit (Family Editor for family parameters, Project Parameters for project bindings), then run the setup again.';
  }
  if (hasFamilyConflict) {
    return ' Resolve the conflict in Revit: rebind the parameter in the Family Editor, or remove the duplicate family or clear its classification value, then run the setup again.';
  }
  return ' Resolve the conflict in Revit: remove the existing project parameter binding so PipeEX can recreate it, then run the setup again.';
}

export function formatMappingParameterConflictMessage(
  requirements: MappingParameterScopeRequirement[],
  assessments: MappingParameterAssessment[]
): string | null {
  const requirementsByKey = new Map(requirements.map((requirement) => [requirement.key, requirement]));
  const conflictedRequirements = new Set<MappingParameterScopeRequirement>();
  const details = assessments
    .filter((assessment) => assessment.status === 'conflict')
    .flatMap((assessment) => {
      const requirement = requirementsByKey.get(assessment.requirementKey);
      if (!requirement) {
        return [];
      }
      conflictedRequirements.add(requirement);
      return assessment.conflicts.map((conflict) => {
        const location = describeRequirementTarget(requirement);
        if (conflict.reason === 'binding') {
          const actual = conflict.actualBindingKind === 'instance' ? 'Instance' : 'Type';
          const expected = requirement.bindingKind === 'instance' ? 'Instance' : 'Type';
          return `"${requirement.parameterName}" on ${conflict.entityName} (${location}) is ${actual}, but ${expected} is required`;
        }
        return `"${requirement.parameterName}" on ${conflict.entityName} (${location}) has an incompatible data type`;
      });
    });
  if (details.length === 0) {
    return null;
  }
  const visible = details.slice(0, 4).join('; ');
  const suffix = details.length > 4 ? `; +${details.length - 4} more conflict${details.length - 4 === 1 ? '' : 's'}` : '';
  return `Parameter conflict: ${visible}${suffix}. PipeEX will not rebind conflicting parameters automatically.${describeConflictResolution(conflictedRequirements)}`;
}

export function selectUnsatisfiedRepairableRequirements(
  requirements: MappingParameterScopeRequirement[],
  assessments: MappingParameterAssessment[]
): MappingParameterScopeRequirement[] {
  const assessmentByKey = new Map(
    assessments.map((assessment) => [assessment.requirementKey, assessment])
  );
  return requirements.filter((requirement) => {
    const assessment = assessmentByKey.get(requirement.key);
    return assessment?.satisfied !== true && assessment?.status !== 'conflict';
  });
}

function referenceMatchesNames(
  reference: MappingProjectParameterReference,
  parameterNames: string[]
): boolean {
  const lookup = new Set(parameterNames.flatMap((name) =>
    getEquivalentRevitParameterNames(name).map(normalizeName)
  ));
  return getEquivalentRevitParameterNames(reference.name).some((name) =>
    lookup.has(normalizeName(name))
  );
}

export function reconcileScopedMissingParameters(
  diff: MappingProjectReadinessDiff,
  requirements: MappingParameterScopeRequirement[],
  satisfaction: MappingParameterScopeSatisfaction[],
  options: {
    preserveCatalogSatisfiedRepairKinds?: MappingProjectParameterReference['repairKinds'];
    preserveCatalogSatisfiedParameterNames?: string[];
  } = {}
): MappingProjectReadinessDiff {
  if (requirements.length === 0) {
    return diff;
  }

  const baseMissingNames = diff.missing.map((reference) => reference.name);
  const preservedRepairKinds = new Set(options.preserveCatalogSatisfiedRepairKinds ?? []);
  const preservedParameterNames = options.preserveCatalogSatisfiedParameterNames ?? [];
  const resultByKey = new Map(satisfaction.map((result) => [result.requirementKey, result]));
  const applicableRequirements = (reference: MappingProjectParameterReference) =>
    requirements.filter((requirement) =>
      requirementParameterIdentity(reference.name) ===
      requirementParameterIdentity(requirement.parameterName)
    );
  const missing = diff.references.flatMap((reference) => {
    const applicable = applicableRequirements(reference);
    if (applicable.length === 0) {
      return referenceMatchesNames(reference, baseMissingNames) ? [reference] : [];
    }
    const catalogAlreadySatisfied = !referenceMatchesNames(reference, baseMissingNames);
    if (
      catalogAlreadySatisfied &&
      reference.repairKinds.some((kind) => preservedRepairKinds.has(kind)) &&
      referenceMatchesNames(reference, preservedParameterNames)
    ) {
      return [];
    }
    const unsatisfied = applicable
      .map((requirement) => resultByKey.get(requirement.key))
      .filter((result) => result?.satisfied !== true);
    if (unsatisfied.length === 0) {
      return [];
    }
    const hasConflict = unsatisfied.some((result) =>
      result && 'status' in result && result.status === 'conflict'
    );
    return [hasConflict ? { ...reference, repairKinds: [] } : reference];
  });

  return {
    references: diff.references,
    missing,
    repairable: missing.filter((reference) => reference.repairKinds.length > 0),
    blocked: missing.filter((reference) => reference.repairKinds.length === 0),
  };
}
